<template>
  <modal id="addUser"
         v-model="visible"
         :title="$t('a-usersForm:createUser')"
         :callback="createUser"
  >
    <small slot="modal-header-right" class="text-muted font-italic">{{ $t('a-usersForm:required') | capitalize }}</small>

    <div slot="modal-body">
      <!-- Fake input to prevent saved password to be filled -->
      <input id="fakeEmail" type="email" class="d-none">
      <input id="fakePassword" type="password" class="d-none">

      <div class="form-group">
        <label for="login" class="control-label">{{ $t('a-usersForm:login') | capitalize }} *</label>
        <input id="login"
               v-model="login"
               v-validate="'required|text'"
               :class="{ 'is-invalid': errors.has('login') }"
               :data-vv-as="$t('a-usersForm:login')"
               type="text"
               name="login"
               class="form-control"
        >
        <div class="invalid-feedback">{{ errors.first('login') }}</div>
      </div>

      <div class="form-group">
        <label for="email" class="control-label">{{ $t('a-usersForm:email') | capitalize }} *</label>
        <input id="email"
               v-model="email"
               v-validate="'required|email'"
               :class="{ 'is-invalid': errors.has('email') }"
               :data-vv-as="$t('a-usersForm:email')"
               type="email"
               name="email"
               class="form-control"
        >
        <div class="invalid-feedback">{{ errors.first('email') }}</div>
      </div>

      <div v-if="isRole.superAdmin" class="form-group">
        <label for="customer" class="control-label">{{ $t('a-usersForm:customer') | capitalize }} *</label>
        <select id="customer"
                v-model="customer"
                v-validate="{rules: {required:isRole.superAdmin}}"
                :class="{ 'is-invalid': errors.has('customer') }"
                :data-vv-as="$t('a-usersForm:customer')"
                name="customer"
                class="form-control"
        >
          <option value="" />
          <option v-for="c in $store.getters.customers" :key="c.id" :value="c" :data-customer-id="c.id">{{ c.name }}</option>
        </select>
        <div class="invalid-feedback">{{ errors.first('customer') }}</div>
      </div>

      <div class="form-group">
        <label for="role">{{ $t('a-usersFor:role') | capitalize }} *</label>
        <select id="role"
                v-model="role"
                v-validate="'required'"
                :class="{ 'is-invalid': errors.has('role') }"
                :data-vv-as="$t('a-usersForm:role')"
                name="role"
                class="form-control"
        >
          <option value="" />
          <option v-for="r in availableRoles" :key="r.level" :value="r.name">{{ r.name }}</option>
        </select>
        <div class="invalid-feedback">{{ errors.first('role') }}</div>
      </div>

      <div class="form-group position-relative">
        <label for="password" class="control-label">{{ $t('a-usersForm:password') | capitalize }} *</label>
        <input id="password"
               ref="password"
               v-model="password"
               v-validate="'required'"
               :class="{ 'is-invalid': errors.has('password') }"
               :data-vv-as="$t('a-usersForm:password')"
               type="password"
               name="password"
               class="form-control"
        >
        <password-strength :password="password" />
        <div class="invalid-feedback">{{ errors.first('password') }}</div>
      </div>

      <div class="form-group">
        <label for="cpassword" class="control-label">{{ $t('a-usersForm:rePassword') | capitalize }} *</label>
        <input id="cpassword"
               v-model="cpassword"
               v-validate="'required|confirmed:password'"
               :class="{ 'is-invalid': errors.has('cpassword') }"
               :data-vv-as="$t('a-usersForm:password')"
               type="password"
               name="cpassword"
               class="form-control"
        >
        <div class="invalid-feedback">{{ errors.first('cpassword') }}</div>
      </div>

      <div class="form-group">
        <label for="expirationDate" class="control-label">{{ $t('a-usersForm:expirationDate') | capitalize }}</label>
        <div class="input-group">
          <flat-pickr id="expirationDate"
                      v-model="expirationDate"
                      :config="fpConfig"
                      class="form-control"
          />
          <div class="input-group-append">
            <button id="resetDateButton" class="btn btn-flat btn-sm" type="button" @click="expirationDate = null"><i class="icon-remove" /></button>
          </div>
        </div>
      </div>

      <div class="form-group">
        <label class="control-label d-block">{{ $t('a-usersForm:activation') | capitalize }}</label>
        <input id="activationEnabled"
               v-model="enabled"
               v-validate="'required'"
               :data-vv-as="$t('a-usersForm:activation')"
               :class="{ 'is-invalid': errors.has('enabled') }"
               :value="true"
               type="radio"
               name="enabled"
        >
        <label for="activationEnabled" class="control-label">{{ $t('a-usersForm:activationEnabled') | capitalize }}</label>

        <input id="activationDisabled"
               v-model="enabled"
               :class="{ 'is-invalid': errors.has('enabled') }"
               :value="false"
               type="radio"
               name="enabled"
        >
        <label for="activationDisabled" class="control-label">{{ $t('a-usersForm:activationDisabled') | capitalize }}</label>

        <div class="invalid-feedback">{{ errors.first('enabled') }}</div>
      </div>
    </div>
  </modal>
</template>

<script>
import { filter, pick, find } from 'lodash-es'
import to from '@/modules/await-to-js'
import eventBus from '@/modules/eventBus'

export default {
  model: {
    prop: 'show',
    event: 'change'
  },

  props: {
    // Display or hide modal
    // To be used as v-model
    show: {
      type: Boolean,
      required: true
    }
  },

  data () {
    return {
      // Display or hide b-modal
      // It cannot change our props, so we need another variable
      visible: false,
      // Form
      login: '',
      email: '',
      customer: '',
      role: '',
      password: '',
      cpassword: '',
      enabled: true,
      expirationDate: null,

      // In data to be modified if needed
      fpConfig: {
        wrap: true,
        altInput: true,
        altFormat: 'Y/m/d',
        dateFormat: 'U',
        minDate: Date.now() + 1000 * 60 * 60 * 24
      }
    }
  },

  computed: {
    authenticatedUserRole () {
      return find(this.$store.getters.roles, role => role.name === this.$store.getters.authenticatedUser.role) || {}
    },

    availableRoles () {
      return filter(this.$store.getters.roles, role => role.level <= this.authenticatedUserRole.level)
    }
  },

  watch: {
    // Synchronize show and visible
    // Use change event to update v-model
    show () {
      this.reset()
      this.visible = this.show

      if (this.show && this.isRole.superAdmin) {
        this.$store.dispatch('getCustomers')
      }
    },
    visible () {
      this.$emit('change', this.visible)
    }
  },

  methods: {
    async reset () {
      this.login = ''
      this.email = ''
      this.customer = ''
      this.role = ''
      this.password = ''
      this.cpassword = ''
      this.expirationDate = null

      // Reset form validation
      await this.$nextTick()
      this.$validator.reset()
    },

    async createUser () {
      const valid = await this.$validator.validateAll()
      if (!valid) return

      if (!this.isRole.superAdmin) {
        this.customer = this.$store.getters.authenticatedUserCustomer
      }

      const user = pick(this, ['login', 'email', 'role', 'password', 'enabled'])
      user.expirationDate = this.expirationDate ? this.expirationDate * 1000 : null
      user.customer = { id: this.customer.id }

      let error
      [error] = await to(this.$api.createUser(this.$store.getters.application, user))
      if (error) return

      this.visible = false
      // Refresh list
      this.$emit('success')
      // Success
      eventBus.$emit('message-success', this.$t('a-usersForm:success', { username: this.login }))
      // Reset form
      this.reset()
    }
  }
}
</script>
